import { useCallback, useEffect, useRef } from 'react';
import usePrevious from 'react-use/esm/usePrevious';
import { useRecoilCallback } from 'recoil';
import { generationStateAtom, escapeHatchDegradedAtom } from './state';
import { TAB_ID } from './tabId';
import {
  isLadderExhausted,
  proposeBump,
  BACKOFF_CEILING,
  BUMP_AFTER_FAILED_RECREATES,
  type GenerationState,
} from './policy';
import {
  updateRegistry,
  countEligible,
  OBSERVER_TTL_MS,
  type ObserverEntry,
  type ObserverRegistry,
} from './registry';
import { evaluateBump } from './decision';
import { logger } from 'modules/logger';
import {
  readFailedRecreatesDetailed,
  writeFailedRecreates,
  clearFailedRecreates,
} from './failedRecreateStore';
import { readUnmetDemandSince } from './unmetDemandStore';
import {
  judgeBumpRecovery,
  BUMP_RECOVERY_DEADLINE_MS,
  type PendingBumpVerification,
} from './bumpVerification';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { OutboundMessageTypes } from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import {
  getCommitChannel,
  subscribeToCommitChannel,
} from 'modules/viewer/workers/workerSelfRetirement';

// VX-4302 #5b: the automatic ladder trigger. This observer counts consecutive failed connection
// recreates (delivery never restored — VX-4284 withholds the worker's pong on a delivery stall, so
// the watchdog recreate fires and, if delivery stays dead, fires again). At N it becomes
// ladder-exhausted and votes; presence + standing votes are shared cross-tab over the single shared
// escape-hatch BroadcastChannel instance (VX-4391 — reuses workerSelfRetirement's commit/ack channel
// rather than opening a duplicate); when quorum (min(2, eligible)) of exhausted observers agree and
// the cooldown allows, it commits a generation bump on the durable atom (which then drives the
// reconnect and worker self-retirement). All of the math is the tested pure policy/registry/decision;
// this hook is the thin adapter that feeds events in and commits the decision out.

// Re-exported so the stores that key evidence per tab (missedPongStore, unmetDemandStore) can use the
// SAME observer identity as the failed-recreate counter they feed — the two must always agree on
// whose evidence they hold.
export { TAB_ID };

// Continuous delivery must hold for this long before we wind the escalation (backoffLevel/lastBumpAt)
// back down — longer than one recreate cycle so a flapping outage does not reset the backoff on every
// brief blip.
const BACKOFF_RESET_DWELL_MS = 2 * 60_000;

// VX-4364: a healthy tab only publishes on a state transition (see observeFrame's skip-publish
// branch below), then goes quiet — so without a periodic re-publish it ages out of peers' registries
// after OBSERVER_TTL_MS even while it still has real frame demand, shrinking countEligible and making
// a 2-of-2 quorum collapse to a false-positive solo bump. This interval re-publishes the own entry
// while frames have actually been arriving recently. Derived from OBSERVER_TTL_MS (rather than a
// separate literal) so the two constants can't drift apart if the TTL is retuned later; must stay
// well under it so the republish always lands before the entry would otherwise go stale.
export const HEARTBEAT_INTERVAL_MS = OBSERVER_TTL_MS / 2.5;

interface CoordinationMessage {
  origin: string;
  entry: ObserverEntry;
}

// VX-4391: this channel also carries generation commits ({ generation }) and retirement acks
// ({ retiredGeneration }) for the drain gate (see generationDrain.ts) — so a payload is only a
// coordination message once it structurally matches this shape.
function parseCoordinationMessage(data: unknown): CoordinationMessage | null {
  const msg = data as Partial<CoordinationMessage> | null;
  if (msg == null || typeof msg.origin !== 'string') return null;
  const entry = msg.entry as Partial<ObserverEntry> | null;
  // Validate the fields registry.ts actually reads (tabId as the map key, generation/lastSeen as
  // numbers) so a commit/ack payload that happened to carry an `entry`-shaped field — or any other
  // malformed data on this now-shared channel — can never reach updateRegistry and crash there.
  if (
    entry == null ||
    typeof entry.tabId !== 'string' ||
    typeof entry.generation !== 'number' ||
    typeof entry.lastSeen !== 'number'
  ) {
    return null;
  }
  return msg as CoordinationMessage;
}

type ObservableWorker = {
  port: Pick<MessagePort, 'addEventListener' | 'removeEventListener'>;
} | null;

type ObservablePort = NonNullable<ObservableWorker>['port'];

// A frame counts as delivery whether it arrives standalone or wrapped in a `batched-messages`
// envelope (the common case on the hot path). Compared against the worker's own enum so a renamed
// frame type is caught at compile time rather than silently looking like a stall.
const isFrameMessage = (type: unknown): boolean =>
  type === OutboundMessageTypes.HandleFrame || type === OutboundMessageTypes.HandleInitialFrame;

const isFrameDelivery = (data: unknown): boolean => {
  const msg = data as { type?: string; messages?: Array<{ type?: string } | null> } | null;
  if (msg == null) return false;
  if (isFrameMessage(msg.type)) return true;
  if (msg.type === OutboundMessageTypes.BatchedMessages && Array.isArray(msg.messages)) {
    return msg.messages.some((m) => isFrameMessage(m?.type));
  }
  return false;
};

// Decide whether to bump right now and, if so, the next generation. Pure — the caller commits it.
function planBump(
  state: GenerationState,
  registry: ObserverRegistry,
  now: number
): GenerationState | null {
  return evaluateBump({ generationState: state, registry, now }) ? proposeBump(state, now) : null;
}

export interface EscapeHatchCoordination {
  // Called by the watchdog each time it recreates the connection without restoring delivery.
  reportFailedRecreate: () => void;
}

export function useEscapeHatchCoordination({
  matchingWorker,
  enabled,
  activeGeneration,
}: {
  matchingWorker: ObservableWorker;
  enabled: boolean;
  // VX-4754: the generation this tab is actually connected at, passed in rather than subscribed to so
  // this hook still holds no Recoil subscription. An increase is the arming signal for the post-bump
  // recovery verification below, and it covers a locally committed bump, a peer-adopted one and the
  // manual override identically.
  activeGeneration: number;
}): EscapeHatchCoordination {
  // Genuine local mutable state (not Recoil mirrors): the failed-recreate counter, this tab's view
  // of the cross-tab registry, and the pending sustained-delivery dwell timer. The counter is
  // mirrored to sessionStorage (VX-4834) so it survives the hard page reload every case-open
  // performs; the ref stays the read path so the frame hot path never touches storage.
  const failedRecreateRef = useRef(0);
  const registryRef = useRef<ObserverRegistry>({});
  const backoffResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // VX-4364: epoch ms of the last delivered frame, so the heartbeat can tell real ongoing demand
  // (frames keep arriving) apart from a mounted-but-idle/fully-loaded tab (they stop).
  const lastFrameAtRef = useRef(0);
  // VX-4754: the bump awaiting a recovery verdict, and its deadline timer. Null between incidents.
  const pendingVerificationRef = useRef<PendingBumpVerification | null>(null);
  const verificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // VX-4754: the port that was attached when the bump was observed — i.e. the PRE-bump one. A frame on
  // that port cannot prove the new generation delivers, so it must not settle the verification. This
  // window is real: the arm effect below and the caller's reconnect effect run in the same commit
  // (this hook is called first, so it arms first), and the listener stays on the old port until the
  // caller's `matchingWorker` actually flips to null.
  const armedOnPortRef = useRef<ObservablePort | null>(null);

  // VX-4834: the single write path for the counter — keeps the in-memory ref and the reload-durable
  // record in lockstep. A zero clears the record outright rather than storing it: "no failures
  // observed" is the default a cold read already yields, so there is nothing to preserve.
  const setFailedRecreates = useCallback((count: number, generation: number, now: number) => {
    const previous = failedRecreateRef.current;
    failedRecreateRef.current = count;
    // VX-4834 follow-up: this module emitted nothing at all, which is why EXP-011's runs could show a
    // wedge, three ladder increments and no bump without revealing which rung stalled. Log every
    // transition — logger forwards to Datadog on any non-local deploy. A drop to 0 is the
    // "delivery recovered, ladder correctly restarted" branch, and is the alternative explanation to
    // the TTL discarding evidence that readFailedRecreatesDetailed reports on the read side.
    // Skip no-op transitions. observeFrame calls this with 0 on the first frame of every page (its
    // steady-state skip only short-circuits once the tab holds a current-generation registry entry),
    // so logging 0 -> 0 produced ~350 lines of noise per run and buried the transitions that matter.
    if (previous !== count) {
      logger.info(
        `SW ladder failedRecreates ${previous} -> ${count}/${BUMP_AFTER_FAILED_RECREATES} ` +
          `at generation=${generation}${count === 0 ? ' (reset — delivery proven or lineage voided)' : ''}`
      );
    }
    if (count === 0) {
      clearFailedRecreates({ tabId: TAB_ID });
      return;
    }
    writeFailedRecreates({ tabId: TAB_ID, generation, count, now });
  }, []);

  // VX-4834: adopt any evidence this tab recorded before the reload. Done once on mount (rather than
  // lazily on first read) so observeFrame's steady-state skip — which reads the ref directly on the
  // frame hot path — is never making a decision against an unhydrated counter. The store itself
  // discards anything recorded against a superseded generation or older than its TTL, so a bump or a
  // long-resolved wedge still starts the ladder from zero.
  const hydrateFailedRecreates = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const { generation } = snapshot.getLoadable(generationStateAtom).getValue();
        const read = readFailedRecreatesDetailed({
          tabId: TAB_ID,
          generation,
          now: Date.now(),
        });
        failedRecreateRef.current = read.count;
        // The decisive diagnostic. `ttl-expired` with a non-zero discardedCount means the ladder HAD
        // evidence and the window threw it away — i.e. increments are arriving further apart than
        // FAILED_RECREATE_EVIDENCE_TTL_MS, which is the open question from soft-pink-dicom (3
        // increments ~25 minutes apart against a 6-minute window, no bump). Silence here, with a
        // reset logged by setFailedRecreates instead, means delivery recovered and the ladder was
        // right to start over. Only logged when something was actually discarded, so a cold start is
        // not noise.
        if (read.discarded != null && read.discarded !== 'absent') {
          logger.warn(
            `SW ladder hydrate discarded evidence (${read.discarded}, count=${read.discardedCount}) ` +
              `at generation=${generation}`
          );
        }
      },
    []
  );

  useEffect(() => {
    if (!enabled) return;
    hydrateFailedRecreates();
  }, [enabled, hydrateFailedRecreates]);

  // Record our own entry in the local registry and broadcast it to peers. Takes the generation
  // explicitly so it holds no Recoil subscription; callers read it from their snapshot.
  const publishEntry = useCallback(
    (generation: number, over: Pick<ObserverEntry, 'hasDemand' | 'exhausted'>, now: number) => {
      const entry: ObserverEntry = { tabId: TAB_ID, generation, lastSeen: now, ...over };
      registryRef.current = updateRegistry(registryRef.current, entry, now);
      getCommitChannel()?.postMessage({ origin: TAB_ID, entry } as CoordinationMessage);
    },
    []
  );

  // Commit a bump if the registry + cooldown warrant one. Reads and writes Recoil through the
  // callback's snapshot/set, so there is no ref mirror of the atom.
  const commitBumpIfWarranted = useRecoilCallback(
    ({ snapshot, set }) =>
      (now: number) => {
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        const next = planBump(state, registryRef.current, now);
        if (next == null) return;
        set(generationStateAtom, next);
        setFailedRecreates(0, next.generation, now);
        // Attention ladder: once bumps reach the ceiling and delivery is still down, surface the
        // ambient indicator. It clears on the next delivered frame (see observeFrame).
        if (next.backoffLevel >= BACKOFF_CEILING) set(escapeHatchDegradedAtom, true);
        analytics.track(viewer.sys.escapeHatchBump, {
          reason: 'ladder-exhausted',
          generation: next.generation,
          eligibleObservers: countEligible(registryRef.current, now, OBSERVER_TTL_MS),
        });
      },
    [setFailedRecreates]
  );

  const reportFailedRecreate = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        if (!enabled) return;
        const now = Date.now();
        // Delivery is interrupted again — cancel any pending sustained-delivery backoff reset.
        if (backoffResetTimerRef.current != null) {
          clearTimeout(backoffResetTimerRef.current);
          backoffResetTimerRef.current = null;
        }
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        setFailedRecreates(failedRecreateRef.current + 1, state.generation, now);
        const exhausted = isLadderExhausted(failedRecreateRef.current);
        publishEntry(state.generation, { hasDemand: true, exhausted }, now);
        // Surface the indicator on ANY exhausted tab once the lineage has reached the ceiling —
        // including one that only adopted the bumped generation from a peer (it never runs the commit
        // path's ceiling branch, so without this its radiologist would see no indicator).
        if (exhausted && state.backoffLevel >= BACKOFF_CEILING) set(escapeHatchDegradedAtom, true);
        commitBumpIfWarranted(now);
      },
    [enabled, publishEntry, commitBumpIfWarranted, setFailedRecreates]
  );

  // A peer's entry just arrived: fold it into the registry and re-evaluate, since it may newly
  // satisfy quorum.
  const handleIncoming = useCallback(
    (entry: ObserverEntry) => {
      const now = Date.now();
      registryRef.current = updateRegistry(registryRef.current, entry, now);
      commitBumpIfWarranted(now);
    },
    [commitBumpIfWarranted]
  );

  // VX-4754: settle the pending post-bump verification and report the verdict. `frameAt` is the epoch
  // ms of the first frame delivered at the bumped generation, or null when the verification is
  // resolving without one. Emitted to RUM on every outcome — including `recovered`, since "the bump
  // worked, in this many ms" is what makes the failure cases legible as the exception rather than the
  // only thing ever reported.
  const resolveVerification = useCallback(
    (frameAt: number | null, now: number, { supersededByBump = false } = {}) => {
      const pending = pendingVerificationRef.current;
      if (pending == null) return;
      pendingVerificationRef.current = null;
      armedOnPortRef.current = null;
      if (verificationTimerRef.current != null) {
        clearTimeout(verificationTimerRef.current);
        verificationTimerRef.current = null;
      }
      // Three independent witnesses that this tab really was waiting on a frame, because no single one
      // is reliable at resolve time. The unmet-demand record is the direct signal, read at the bumped
      // generation (the watchdog voids it on a bump and re-marks it on its next 30s tick) — but
      // `recreateWorker` clears it on EVERY recreate, so a resolve that lands in the window between a
      // recreate and the next tick would read a starved tab as idle and downgrade a real failure to
      // `no-demand`. So a failed recreate at this generation counts too (the counter is reset by the
      // bump, so any non-zero value was earned since it, and only a starved loading stack produces
      // one), and a superseding bump counts unconditionally — the ladder re-exhausting is itself proof
      // both that demand persisted and that this bump did not answer it.
      const hasUnmetDemand =
        supersededByBump ||
        failedRecreateRef.current > 0 ||
        readUnmetDemandSince({ tabId: TAB_ID, generation: pending.generation, now }) != null;
      const { outcome, elapsedMs } = judgeBumpRecovery({ pending, frameAt, hasUnmetDemand, now });
      const line =
        `SW bump recovery ${outcome} after ${Math.round(elapsedMs / 1000)}s ` +
        `for tab=${TAB_ID} at generation=${pending.generation}`;
      if (outcome === 'unrecovered') logger.warn(line);
      else logger.info(line);
      analytics.track(viewer.sys.escapeHatchBumpOutcome, {
        outcome,
        generation: pending.generation,
        elapsedMs,
      });
    },
    []
  );

  // VX-4754: arm a verification on every observed generation increase. A bump landing while a prior
  // verification is still pending resolves that one immediately: the ladder re-exhausted and bumped
  // again, which is itself proof the earlier bump never restored delivery — no need to wait out its
  // deadline.
  const previousGeneration = usePrevious(activeGeneration);
  useEffect(() => {
    if (!enabled) return;
    // undefined on the first render, so a page that loads at an already-bumped generation does not
    // judge a bump it never saw happen. Only an increase arms — a drop to 0 is the flag being turned
    // off, not a recovery event.
    if (previousGeneration === undefined || activeGeneration <= previousGeneration) return;
    const now = Date.now();
    resolveVerification(null, now, { supersededByBump: true });
    pendingVerificationRef.current = { generation: activeGeneration, bumpedAt: now };
    // Null when nothing is attached yet, in which case any frame that arrives is already post-bump.
    armedOnPortRef.current = matchingWorker?.port ?? null;
    verificationTimerRef.current = setTimeout(() => {
      verificationTimerRef.current = null;
      resolveVerification(null, Date.now());
    }, BUMP_RECOVERY_DEADLINE_MS);
    // `matchingWorker` is read, not tracked: the guard above means a re-run triggered by the reconnect
    // (same generation) returns before it could overwrite the armed port with the post-bump one.
  }, [enabled, activeGeneration, previousGeneration, resolveVerification, matchingWorker]);

  // A page teardown is not a verdict — drop the pending verification silently rather than reporting a
  // failure for a tab that simply navigated away mid-window.
  useEffect(
    () => () => {
      if (verificationTimerRef.current != null) clearTimeout(verificationTimerRef.current);
      verificationTimerRef.current = null;
      pendingVerificationRef.current = null;
    },
    []
  );

  // A delivered frame (standalone or batched) means delivery is healthy again. Takes the port it
  // arrived on so the bump verification can tell a post-bump frame from a late pre-bump one; the
  // ladder reset below applies either way — any frame at all disproves a total wedge.
  const observeFrame = useRecoilCallback(
    ({ snapshot, set }) =>
      (deliveringPort: ObservablePort) => {
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        const now = Date.now();
        lastFrameAtRef.current = now;
        // VX-4754: resolved before the steady-state skip below, so the verdict is never lost to it. A
        // frame on the port the verification was armed on is a late delivery from the SUPERSEDED
        // generation — it says nothing about whether the fresh worker delivers, so it neither settles
        // the verification as recovered nor counts against it; the deadline still governs.
        if (deliveringPort !== armedOnPortRef.current) resolveVerification(now, now);
        // Once we have escalated (backoffLevel > 0), require a sustained run of healthy delivery
        // before winding the escalation back to 0. The timer is that proof: any fresh stall cancels
        // it (in reportFailedRecreate), so it only fires after BACKOFF_RESET_DWELL_MS of
        // uninterrupted frames — at which point the cooldown/indicator basis is safe to reset and a
        // later, unrelated incident starts from a clean slate rather than an inflated backoff.
        if (state.backoffLevel > 0 && backoffResetTimerRef.current == null) {
          backoffResetTimerRef.current = setTimeout(() => {
            backoffResetTimerRef.current = null;
            set(generationStateAtom, (s) =>
              s.backoffLevel === 0 && s.lastBumpAt === 0
                ? s
                : { ...s, backoffLevel: 0, lastBumpAt: 0 }
            );
          }, BACKOFF_RESET_DWELL_MS);
        }
        // Frames stream fast, so skip the publish in the steady state: nothing to clear (no failures,
        // not degraded) and peers already hold a current-generation entry for us. We still publish
        // once after a reconnect (the generation changed, so our prior entry is stale and would
        // inflate peers' eligible count for the whole TTL) and on a recovery transition.
        const degraded = snapshot.getLoadable(escapeHatchDegradedAtom).getValue();
        const ownEntryIsCurrent = registryRef.current[TAB_ID]?.generation === state.generation;
        if (failedRecreateRef.current === 0 && !degraded && ownEntryIsCurrent) return;
        setFailedRecreates(0, state.generation, now);
        if (degraded) set(escapeHatchDegradedAtom, false); // delivery restored — clear the indicator
        publishEntry(state.generation, { hasDemand: true, exhausted: false }, now);
      },
    [publishEntry, setFailedRecreates, resolveVerification]
  );

  // VX-4364: re-publish the own entry on a slow cadence while frames have actually been arriving
  // recently. Gated on lastFrameAtRef rather than firing unconditionally for every mounted tab, so an
  // idle, fully-loaded, or prefetch-finished tab goes quiet and ages out of peers' registries via the
  // normal TTL instead of being blanket-heartbeated forever.
  const heartbeat = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const now = Date.now();
        if (now - lastFrameAtRef.current > HEARTBEAT_INTERVAL_MS) return;
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        const exhausted = isLadderExhausted(failedRecreateRef.current);
        publishEntry(state.generation, { hasDemand: true, exhausted }, now);
      },
    [publishEntry]
  );

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, heartbeat]);

  // Cross-tab presence + votes over the shared escape-hatch channel. Ignore our own echoes and any
  // non-coordination payload (commits/acks — see parseCoordinationMessage).
  useEffect(() => {
    if (!enabled) return;
    return subscribeToCommitChannel((data) => {
      const msg = parseCoordinationMessage(data);
      if (msg == null || msg.origin === TAB_ID) return;
      handleIncoming(msg.entry);
    });
  }, [enabled, handleIncoming]);

  // Delivery observation on the active worker port.
  useEffect(() => {
    if (!enabled || matchingWorker == null) return;
    const { port } = matchingWorker;
    const onMessage = (event: MessageEvent) => {
      if (isFrameDelivery(event.data)) observeFrame(port);
    };
    port.addEventListener('message', onMessage as EventListener);
    return () => {
      port.removeEventListener('message', onMessage as EventListener);
      if (backoffResetTimerRef.current != null) {
        clearTimeout(backoffResetTimerRef.current);
        backoffResetTimerRef.current = null;
      }
    };
  }, [enabled, matchingWorker, observeFrame]);

  return { reportFailedRecreate };
}
